import type { InternalAxiosRequestConfig } from 'axios';
import { AxiosError } from 'axios';
import { getSession } from 'next-auth/react';
import { jwtDecode } from 'jwt-decode';
import { getCurrentTimeInSeconds } from '../../../utils/Time';
import { NEXT_AUTH_REFRESH_ACCESS_TOKEN_ERROR_MESSAGE } from '../../../helpers/constants';
import { logger } from '@dist-marketplace-frontend/services';
import getConfig from 'next/config';

/**
 * JWT payload interface with exp claim
 */
interface JWTPayload {
  exp: number;
  [key: string]: unknown;
}

const WHITELISTED_API_PATHS = [
  '/ddapi/legacy/adwrite/api/v3/create/ad/draft',
  '/ddapi/v1/listings/photos',
  '/ddapi/v1/listings',
  '/ddapi/v2/orders/bundles',
];

/**
 * Extract token from Authorization header
 */
function extractTokenFromHeader(authHeader: unknown): string | undefined {
  // Handle array case (axios can have headers as arrays)
  const headerValue = Array.isArray(authHeader) ? authHeader[0] : authHeader;

  if (typeof headerValue !== 'string' || !headerValue.startsWith('Bearer ')) {
    return undefined;
  }

  return headerValue.substring(7); // Remove 'Bearer ' prefix
}

export function createTokenRefreshInterceptor({
  onSessionRefreshSuccess,
  onSessionRefreshError,
  refreshBufferInSeconds = 10,
}: {
  onSessionRefreshSuccess: () => Promise<void>;
  onSessionRefreshError: () => Promise<void>;
  refreshBufferInSeconds?: number;
}) {
  // Promise to handle concurrent refresh requests
  let refreshPromise: Promise<string | undefined> | null = null;

  /**
   * Axios request interceptor for token refresh and authorization header
   */
  return async function tokenRefreshInterceptor(
    config: InternalAxiosRequestConfig,
  ): Promise<InternalAxiosRequestConfig> {
    const {
      publicRuntimeConfig: { FEATURE_AXIOS_TOKEN_REFRESH_INTERCEPTOR },
    } = getConfig();

    if (!FEATURE_AXIOS_TOKEN_REFRESH_INTERCEPTOR) {
      return config;
    }

    // Only run client-side
    if (typeof window === 'undefined') {
      return config;
    }

    // This allows incrementally deploying this feature to minimize risk
    if (!WHITELISTED_API_PATHS.some((path) => config.url?.includes(path))) {
      return config;
    }

    try {
      // First, check if there's already a token in the Authorization header
      const existingAuthHeader = config.headers.Authorization;

      // If no Authorization header exists, this is an unauthenticated request
      if (existingAuthHeader === undefined) {
        return config;
      }

      const existingToken = extractTokenFromHeader(existingAuthHeader);

      // If we have a token in the header, check if it's still valid
      if (!isTokenExpired(existingToken)) {
        // Token in header is valid, no need to refresh it
        return config;
      }

      // Token in header is expired, trigger refresh
      // refreshSessionToken handles concurrent requests
      // If another refresh request is already in progress,
      // it will wait for it to complete, otherwise it will start a new request
      const accessToken = await refreshSessionToken();

      if (!accessToken) {
        const error = new AxiosError('Unauthorized - No valid session');
        error.config = config;
        error.response = {
          status: 401,
          statusText: 'Unauthorized',
          data: { message: 'Authentication required' },
          headers: {},
          config,
        };
        throw error;
      }

      // Token has been refreshed, use the fresh token
      config.headers.Authorization = `Bearer ${accessToken}`;
    } catch (error) {
      // If it's already an AxiosError (like our 401), re-throw it
      if (error instanceof AxiosError) {
        throw error;
      }

      // For other errors, log and re-throw to prevent the request from proceeding
      logger.error({
        message: 'Error in token refresh interceptor',
        error,
      });
      throw error;
    }

    return config;
  };

  /**
   * Check if token is expired or expiring soon by decoding the JWT token
   */
  function isTokenExpired(token: string | undefined): boolean {
    if (!token) {
      return true;
    }

    try {
      const decoded = jwtDecode<JWTPayload>(token);

      if (!decoded.exp) {
        // If token doesn't have exp claim, consider it expired
        return true;
      }

      const currentTimeInSeconds = getCurrentTimeInSeconds();
      const timeRemaining = decoded.exp - currentTimeInSeconds;

      // Token is expired or will expire within the buffer period
      return timeRemaining <= refreshBufferInSeconds;
    } catch (error) {
      // If we can't decode the token, consider it expired/invalid
      logger.error({
        message: 'Failed to decode JWT token for expiration check',
        error,
      });
      return true;
    }
  }

  /**
   * Refresh the session token by calling getSession
   * Uses a promise queue to prevent concurrent refresh requests
   *
   * Note: getSession() will trigger NextAuth's JWT callback, which checks expiration
   * and refreshes the token if needed. The JWT callback uses expiresAt from the token,
   * so calling getSession() when the token in the header is expired should trigger refresh.
   *
   * @returns The new access token, or undefined if refresh failed
   */
  async function refreshSessionToken(): Promise<string | undefined> {
    const {
      publicRuntimeConfig: { API_CLIENT_TIMEOUT },
    } = getConfig();

    // If a refresh is already in progress, wait for it
    if (refreshPromise) {
      return await refreshPromise;
    }

    // Create a new refresh promise
    const promise = (async (): Promise<string | undefined> => {
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(
          () => reject(new Error('Token refresh timed out')),
          API_CLIENT_TIMEOUT,
        );
      });

      try {
        // getSession() will trigger NextAuth's JWT callback which checks token expiration
        // and refreshes if needed. Since we've already verified the token is expired,
        // this should trigger the refresh.
        const session = await Promise.race([getSession(), timeoutPromise]);

        if (
          session?.error === NEXT_AUTH_REFRESH_ACCESS_TOKEN_ERROR_MESSAGE ||
          !session?.accessToken
        ) {
          throw new Error('Failed to refresh session token');
        }

        // Fire-and-forget: syncing the React session context shouldn't
        // block the original HTTP request or invalidate the fresh token
        onSessionRefreshSuccess().catch((error) => {
          logger.error({
            message: 'onSessionRefreshSuccess callback failed',
            error,
          });
        });

        return session.accessToken;
      } catch (error) {
        // Handle errors from getSession() and the timeout race (network errors, etc.)
        logger.error({
          message: 'Error calling getSession() to refresh token',
          error,
        });
        onSessionRefreshError().catch((error) => {
          logger.error({
            message: 'onSessionRefreshError callback failed',
            error,
          });
        });
        return undefined;
      } finally {
        // Clear the promise so future requests can trigger a new refresh
        refreshPromise = null;
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      }
    })();

    refreshPromise = promise;
    return await promise;
  }
}
